import { useEffect, useState, useCallback } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Search, Globe, ExternalLink, Edit, X, Save } from "lucide-react";
import useAccessibleOverlay from "@/hooks/useAccessibleOverlay";

export default function BusinessWebsites() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [enabledOnly, setEnabledOnly] = useState(false);
  const [editing, setEditing] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const closeEditor = () => { setEditing(null); setSettings(null); };
  const { panelRef } = useAccessibleOverlay(Boolean(editing && settings), closeEditor);

  const load = useCallback(async () => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (enabledOnly) params.set("enabled_only", "true");
    const { data } = await api.get(`/admin/business-websites?${params}`);
    setItems(data.items || []);
  }, [q, enabledOnly]);

  useEffect(() => { load(); }, [load]);

  const loadSettings = async (companyId) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/business-websites/${companyId}`);
      setSettings(data);
      setEditing(companyId);
    } catch (e) {
      toast.error("Failed to load settings");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setLoading(true);
    try {
      await api.put(`/admin/business-websites/${editing}`, settings);
      toast.success("Website settings updated");
      setEditing(null);
      setSettings(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto p-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-display text-2xl font-semibold flex items-center gap-2">
          <Globe className="w-6 h-6" /> Business Websites
        </h1>
      </div>

      <div className="bg-white rounded-lg border p-4 mb-6">
        <div className="flex flex-wrap gap-3">
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search businesses..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm"
            />
          </div>
          <label className="flex items-center gap-2 px-4 py-2 border rounded-lg cursor-pointer hover:bg-gray-50">
            <input
              type="checkbox"
              checked={enabledOnly}
              onChange={(e) => setEnabledOnly(e.target.checked)}
              className="rounded"
            />
            <span className="text-sm">Enabled only</span>
          </label>
        </div>
      </div>

      <div className="bg-white rounded-lg border">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="border-b bg-gray-50">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Business</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Slug</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Status</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Premium</th>
                <th className="text-right px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {items.map((item) => (
                <tr key={item.company_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <div className="font-medium text-gray-900">{item.name}</div>
                    <div className="text-xs text-gray-500">{item.website_headline}</div>
                  </td>
                  <td className="px-4 py-3">
                    {item.public_slug ? (
                      <code className="text-xs bg-gray-100 px-2 py-1 rounded">{item.public_slug}</code>
                    ) : (
                      <span className="text-gray-400 text-xs">No slug</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {item.public_website_enabled ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        Enabled
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                        Disabled
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {item.premium_visibility_active ? (
                      <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800">
                        ⭐ Premium
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => loadSettings(item.company_id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded-lg"
                    >
                      <Edit className="w-4 h-4" /> Edit
                    </button>
                    {item.public_slug && (
                      <a
                        href={`/app/public/rentals/${item.public_slug}`}
                        target="_blank"
                        rel="noreferrer"
                        className="inline-flex items-center gap-1 ml-2 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {items.length === 0 && (
          <div className="text-center py-12 text-gray-500">
            No enterprise businesses found
          </div>
        )}
      </div>

      {editing && settings && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="business-website-editor-title"
               className="bg-white rounded-lg shadow-xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b px-6 py-4 flex items-center justify-between">
              <h2 id="business-website-editor-title" className="text-xl font-semibold">Edit Website Settings: {settings.name}</h2>
              <button type="button" aria-label="Close website settings" onClick={closeEditor} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label htmlFor="admin-website-enabled" className="block text-sm font-medium mb-1">Enable Website</label>
                <input
                  id="admin-website-enabled"
                  type="checkbox"
                  checked={settings.public_website_enabled || false}
                  onChange={(e) => setSettings({ ...settings, public_website_enabled: e.target.checked })}
                  className="rounded"
                />
              </div>

              <div>
                <label htmlFor="admin-website-slug" className="block text-sm font-medium mb-1">Slug</label>
                <input
                  id="admin-website-slug"
                  type="text"
                  value={settings.public_slug || ""}
                  onChange={(e) => setSettings({ ...settings, public_slug: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="business-slug"
                />
              </div>

              <div>
                <label htmlFor="admin-website-headline" className="block text-sm font-medium mb-1">Headline</label>
                <input
                  id="admin-website-headline"
                  type="text"
                  value={settings.website_headline || ""}
                  onChange={(e) => setSettings({ ...settings, website_headline: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  placeholder="Your business headline"
                />
              </div>

              <div>
                <label htmlFor="admin-website-about" className="block text-sm font-medium mb-1">About</label>
                <textarea
                  id="admin-website-about"
                  value={settings.website_about || ""}
                  onChange={(e) => setSettings({ ...settings, website_about: e.target.value })}
                  className="w-full px-3 py-2 border rounded-lg"
                  rows={4}
                  placeholder="About your business..."
                />
              </div>

              <div>
                <label htmlFor="admin-website-theme" className="block text-sm font-medium mb-1">Theme Color</label>
                <input
                  id="admin-website-theme"
                  type="color"
                  value={settings.website_theme_color || "#1B4332"}
                  onChange={(e) => setSettings({ ...settings, website_theme_color: e.target.value })}
                  className="w-20 h-10 rounded"
                />
              </div>
            </div>

            <div className="sticky bottom-0 bg-gray-50 border-t px-6 py-4 flex justify-end gap-3">
              <button
                onClick={closeEditor}
                className="px-4 py-2 text-gray-700 hover:bg-gray-200 rounded-lg"
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={saveSettings}
                disabled={loading}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-lg disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {loading ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
